import MuiButton, { ButtonProps as MuiButtonProps } from '@mui/material/Button';
import clsx from 'clsx';
import { ElementType, forwardRef, ReactNode } from 'react';
import { Tooltip } from '../display';
import Icon, { IconName } from '../display/Icon';
import Spinner from '../display/Spinner';
import { useAsyncHandler } from '../utils';
import classes from './Button.module.scss';

const TYPE_DEFAULTS = {
  normal: { variant: 'contained' },
  primary: { color: 'primary', variant: 'contained' },
  primaryText: { color: 'primary', variant: 'text' },
  secondary: { color: 'controlText', variant: 'text' },
  submit: { color: 'primary', variant: 'contained' },
  context: { color: 'controlText', size: 'small', variant: 'text' },
};

export default forwardRef(function Button(
  {
    children,
    className,
    component,
    disabled,
    disabledTooltip,
    errorMessage,
    icon,
    onClick,
    type = 'normal',
    ...rest
  }: ButtonProps,
  ref,
) {
  const buttonProps = { ...TYPE_DEFAULTS[type], ...rest };
  const htmlType = type === 'submit' ? 'submit' : 'button';
  const tooltipText = disabled && disabledTooltip;

  const [clickHandler, isProcessing] = useAsyncHandler({ handler: onClick });

  return (
    <Tooltip title={tooltipText}>
      <MuiButton
        {...(buttonProps as any)}
        className={clsx(classes.root, className)}
        component={disabled ? 'div' : component}
        disabled={isProcessing || disabled}
        onClick={clickHandler}
        ref={ref}
        type={htmlType}
      >
        {icon && !isProcessing && <Icon icon={icon} />}
        {isProcessing && <Spinner />}

        {children}
      </MuiButton>
    </Tooltip>
  );
});

export interface ButtonProps extends Omit<MuiButtonProps, 'type'> {
  component?: ElementType;
  disabledTooltip?: ReactNode;
  errorMessage?: string;
  icon?: IconName;
  type?: keyof typeof TYPE_DEFAULTS;
}
